import React, { useState, useEffect } from "react";
import ActivePickupStep from "./ActivePickupStep";
import ActiveDropoffStep from "./ActiveDropoffStep";
import { Package, Truck, CircleCheck } from "lucide-react";
import { theme } from "../assets/theme/theme";

const ActiveRidePage = ({
  activeRide,
  GetRideDetails,
  mobile,
  onGetNewRides,
}) => {
  const [started, setStarted] = useState(true);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [isThisLastStep, setIsThisLastStep] = useState(false);
  const [rideCompleted, setRideCompleted] = useState(false);

  /* ---------------- LOAD STEPS ---------------- */
  useEffect(() => {
    if (activeRide?.steps?.length) {
      setSteps(activeRide.steps);
    } else {
      setSteps([]);
    }
  }, [activeRide]);

  /* ---------------- DETERMINE CURRENT STEP ---------------- */
  useEffect(() => {
    if (!steps.length) {
      setCurrentStep(null);
      return;
    }

    const nextStep = steps.find((step) => step.status !== "COMPLETED");
    setCurrentStep(nextStep || null);
    setIsThisLastStep(
      nextStep ? steps.indexOf(nextStep) === steps.length - 1 : false
    );
  }, [steps]);

  /* ---------------- STEP COMPLETION HANDLER ---------------- */
  const handleStepCompleted = async () => {
    if (isThisLastStep) {
      setRideCompleted(true);
    } else {
      setRideCompleted(false);
      await GetRideDetails(mobile);
    }
  };

  const isRideCompleted =
    steps.length > 0 && steps.every((step) => step.status === "COMPLETED");

  const completedStepsCount = steps.filter((s) => s.status === "COMPLETED").length;
  const progressPercent = steps.length ? Math.round((completedStepsCount / steps.length) * 100) : 0;

  /* ---------------- STICKY COMPACT HEADER ---------------- */
  const RideHeader = () => (
    <div
      className="sticky top-0 z-20 border-b shadow-xs px-4 py-3"
      style={{
        backgroundColor: theme.colors.pureWhite,
        borderColor: theme.colors.grey002,
      }}
    >
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2">
            <h2
              className="text-lg font-bold tracking-tight"
              style={{
                fontFamily: "BarlowCondensed-Bold",
                color: theme.colors.bitumen,
              }}
            >
              Shipment #{activeRide?.shipmentNumber || activeRide?.shipmentId || "-"}
            </h2>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{
                fontFamily: "DMMono-Medium",
                backgroundColor: isRideCompleted
                  ? `${theme.colors.success}15`
                  : theme.colors.offWhite,
                color: isRideCompleted
                  ? theme.colors.success
                  : theme.colors.charcoal,
                border: `1px solid ${isRideCompleted ? `${theme.colors.success}30` : theme.colors.grey002}`,
              }}
            >
              {isRideCompleted ? "Delivered" : "In Transit"}
            </span>
          </div>
          <p
            className="text-[11px]"
            style={{
              fontFamily: "DMSans-Regular",
              color: theme.colors.concrete,
            }}
          >
            {completedStepsCount} of {steps.length} milestones {/* complete ({progressPercent}%) */}
          </p>
        </div>

        {isRideCompleted && (
          <CircleCheck className="w-6 h-6 animate-pulse" style={{ color: theme.colors.success }} />
        )}
      </div>

      {/* Progress Bar Track */}
   {/*    <div
        className="w-full h-1 mt-2.5 rounded-full overflow-hidden"
        style={{ backgroundColor: theme.colors.grey005 }}
      >
        <div
          className="h-full transition-all duration-500 rounded-full"
          style={{
            width: `${progressPercent}%`,
            backgroundColor: isRideCompleted
              ? theme.colors.success
              : theme.colors.brand,
          }}
        />
      </div> */}
    </div>
  );

  /* ---------------- START SCREEN (FALLBACK) ---------------- */
  if (!started && !isRideCompleted) {
    return (
      <div className="h-full flex flex-col justify-between">
        <RideHeader />
        <div className="p-4 flex-1 flex flex-col items-center justify-center">
          <div
            className="w-full rounded-2xl p-6 text-center border shadow-sm"
            style={{
              backgroundColor: theme.colors.pureWhite,
              borderColor: theme.colors.grey002,
              borderRadius: `${theme.radius[24]}px`,
            }}
          >
            <div
              className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center text-white shadow-md"
              style={{ backgroundColor: theme.colors.brand }}
            >
              <Package className="w-6 h-6" />
            </div>
            <h3
              className="text-lg font-bold"
              style={{
                fontFamily: "BarlowCondensed-Bold",
                color: theme.colors.bitumen,
              }}
            >
              Ready to Deliver
            </h3>
            <p
              className="text-xs my-2 max-w-xs mx-auto"
              style={{
                fontFamily: "DMSans-Regular",
                color: theme.colors.concrete,
              }}
            >
              This trip has {steps.length} stops. Tap below to navigate to the first collection point.
            </p>

            <button
              onClick={() => setStarted(true)}
              className="w-full mt-4 py-3.5 text-white font-bold transition-all shadow-md active:scale-[0.99] cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                borderRadius: `${theme.radius[16]}px`,
                backgroundColor: theme.colors.brand,
                boxShadow: `0 4px 12px ${theme.colors.brand}40`,
              }}
            >
              Start Ride Protocol
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- RIDE COMPLETED SCREEN ---------------- */
  if (rideCompleted) {
    return (
      <div className="h-full flex flex-col justify-between">
        <RideHeader />
        <div className="p-4 flex-1 flex flex-col items-center justify-center">
          <div
            className="w-full p-8 text-center border shadow-sm"
            style={{
              backgroundColor: theme.colors.pureWhite,
              borderColor: theme.colors.grey002,
              borderRadius: `${theme.radius[24]}px`,
            }}
          >
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shadow-lg animate-bounce"
              style={{ backgroundColor: theme.colors.success }}
            >
              <CircleCheck className="w-8 h-8" />
            </div>

            <h2
              className="text-2xl font-bold"
              style={{
                fontFamily: "BarlowCondensed-Bold",
                color: theme.colors.bitumen,
              }}
            >
              Delivery Fulfilled!
            </h2>

            <p
              className="text-xs mt-1 mb-6 max-w-xs mx-auto"
              style={{
                fontFamily: "DMSans-Regular",
                color: theme.colors.concrete,
              }}
            >
              Shipment #{activeRide?.shipmentNumber || activeRide?.shipmentId} has been successfully completed and recorded.
            </p>

            <button
              onClick={onGetNewRides}
              className="w-full py-4 text-white font-bold transition-all shadow-md active:scale-[0.99] cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                borderRadius: `${theme.radius[16]}px`,
                backgroundColor: theme.colors.brand,
                boxShadow: `0 4px 14px ${theme.colors.brand}40`,
              }}
            >
              Find New Rides
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- ACTIVE STEP UI ---------------- */
  return (
    <div className="h-full flex flex-col">
      <RideHeader />

      <div className="p-3.5 space-y-2 flex-1 flex flex-col justify-start">
        {/* Step Context Sub-bar */}
        {currentStep && (
          <div
            className="flex items-center justify-between px-3 py-2 rounded-xl border"
            style={{
              backgroundColor: theme.colors.pureWhite,
              borderColor: theme.colors.grey002,
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white"
                style={{
                  backgroundColor:
                    currentStep.type === "PICKUP"
                      ? theme.colors.bitumen
                      : theme.colors.primaryRed,
                }}
              >
                {currentStep.type === "PICKUP" ? (
                  <Package className="w-4 h-4" />
                ) : (
                  <Truck className="w-4 h-4" />
                )}
              </div>
              <div>
                <h4
                  className="text-xs font-bold leading-tight"
                  style={{
                    fontFamily: "BarlowCondensed-SemiBold",
                    color: theme.colors.bitumen,
                  }}
                >
                  Step {currentStep.step} of {steps.length} — {currentStep.type}
                </h4>
                {currentStep.refNumber && (
                  <p
                    className="text-[10px]"
                    style={{
                      fontFamily: "DMMono-Regular",
                      color: theme.colors.concrete,
                    }}
                  >
                    Ref: {currentStep.refNumber}
                  </p>
                )}
              </div>
            </div>

            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
              style={{
                fontFamily: "DMSans-Medium",
                backgroundColor: theme.colors.offWhite,
                color: theme.colors.charcoal,
                border: `1px solid ${theme.colors.grey002}`,
              }}
            >
              Active
            </span>
          </div>
        )}

        {/* Step Execution Renderer */}
        {currentStep?.type === "PICKUP" && (
          <ActivePickupStep
            step={currentStep}
            onComplete={handleStepCompleted}
            activeRide={activeRide}
          />
        )}

        {currentStep?.type === "DROPOFF" && (
          <ActiveDropoffStep
            step={currentStep}
            onComplete={handleStepCompleted}
            activeRide={activeRide}
          />
        )}
      </div>
    </div>
  );
};

export default ActiveRidePage;